import { useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { 
  Gift, ShoppingBag, Heart, Sparkles, BookOpen, 
  Video, Calendar, ExternalLink, Star, Package
} from "lucide-react";

const GIFT_BASKETS = [
  {
    id: "mind-spa-retreat",
    name: "RAPS-SMAHoo ME.WELL Mind-SPA Retreat Basket",
    description: "Comprehensive mind and body experience with skincare products and therapeutic resources",
    price: 149.99,
    image: "https://images.unsplash.com/photo-1620077399971-431e7ea0cf0c?w=600",
    category: "premium",
    features: ["Skincare products", "Therapeutic resources", "Mindfulness guide", "Self-care tools"]
  },
  {
    id: "emotional-wellness",
    name: "Emotional Wellness Gift Bowl",
    description: "Curated items for emotional balance and daily self-care rituals",
    price: 79.99,
    image: "https://images.unsplash.com/photo-1620924714065-c6586d8f79eb?w=600",
    category: "wellness",
    features: ["Aromatherapy items", "Journal", "Calming tea", "Affirmation cards"]
  },
  {
    id: "tele-edu-therapy",
    name: "Tele-Edu.Therapy Online Bundle",
    description: "Educational wellness tools and therapeutic resources delivered digitally",
    price: 59.99,
    image: "https://images.unsplash.com/photo-1623652554515-91c833e3080e?w=600",
    category: "digital",
    features: ["eBooks collection", "Video workshops", "Guided meditations", "Check-in tools"]
  },
  {
    id: "stress-relief",
    name: "Adaptive Stress Relief Basket",
    description: "Tools and resources for managing daily stress and building resilience",
    price: 89.99,
    image: "https://images.unsplash.com/photo-1676753900379-8b05048608da?w=600",
    category: "wellness",
    features: ["Stress ball set", "Breathing guide", "Relaxation audio", "Herbal tea"]
  },
  {
    id: "mindfulness-starter",
    name: "Daily Mindfulness Starter Kit",
    description: "Everything you need to begin your mindfulness journey",
    price: 49.99,
    image: "https://images.unsplash.com/photo-1758274525911-402f99afec14?w=600",
    category: "starter",
    features: ["Meditation cushion", "Beginner guide", "Timer app access", "Incense set"]
  },
  {
    id: "corporate-wellness",
    name: "Corporate Wellness Package",
    description: "Year-long personal and corporate mind & emotional wellness program",
    price: 299.99,
    image: "https://images.unsplash.com/photo-1758274538676-87c2314bca60?w=600",
    category: "premium",
    features: ["Team workshops", "Individual check-ins", "Monthly resources", "Progress tracking"]
  }
];

const SERVICES = [
  {
    id: "check-in",
    name: "Self-Motivated Adaptive TT Check-IN Programs",
    description: "Self-paced, adaptive programs to monitor and improve your emotional and mental health",
    icon: Calendar
  },
  {
    id: "tele-therapy",
    name: "Tele-Edu.Therapy Sessions",
    description: "Therapeutic gift baskets, bowls, and cards with education and wellness tools",
    icon: Video
  },
  {
    id: "workshops",
    name: "Monthly Mind & Emotional Health Workshops",
    description: "Practical tools and techniques for maintaining mental and emotional well-being",
    icon: BookOpen
  }
];

const CATEGORY_COLORS = {
  premium: 'bg-amber-100 text-amber-700',
  wellness: 'bg-green-100 text-green-700',
  digital: 'bg-blue-100 text-blue-700',
  starter: 'bg-purple-100 text-purple-700'
};

export default function GiftBasketStore() {
  const [activeCategory, setActiveCategory] = useState("all");

  const categories = [
    { id: "all", label: "All Products" },
    { id: "premium", label: "Premium" },
    { id: "wellness", label: "Wellness" },
    { id: "digital", label: "Digital" },
    { id: "starter", label: "Starter Kits" },
  ];

  const filteredBaskets = activeCategory === "all" 
    ? GIFT_BASKETS 
    : GIFT_BASKETS.filter(b => b.category === activeCategory);

  const openStore = () => {
    window.open('https://www.gift-basket.store', '_blank', 'noopener,noreferrer');
  };

  return (
    <DashboardLayout>
      <div className="p-6 md:p-8 lg:p-12 space-y-8 animate-fade-in" data-testid="gift-basket-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <p className="overline-text mb-2">mySMAHOO.Online</p>
            <h1 className="font-serif text-3xl sm:text-4xl font-light text-spa-moss mb-3">
              Self-CARE Gift Basket Store
            </h1>
            <p className="text-spa-text-secondary max-w-2xl">
              Discover adaptive mental health programs, curated gift baskets, and daily mindfulness 
              resources designed to support your emotional wellness journey.
            </p>
          </div>
          <Button 
            onClick={openStore}
            className="spa-button-primary flex items-center gap-2"
            data-testid="visit-store-button"
          >
            <ExternalLink className="w-4 h-4" /> Visit Full Store
          </Button>
        </div>

        {/* Hero Banner */}
        <div className="spa-card bg-gradient-to-r from-spa-terracotta to-spa-terracotta-dark text-white relative overflow-hidden">
          <div className="grid md:grid-cols-2 gap-6 items-center">
            <div className="relative z-10">
              <div className="flex items-center gap-2 mb-3">
                <Gift className="w-6 h-6" />
                <span className="text-white/80 text-sm">RAPs.ME.WELL Gift Basket Online Store</span>
              </div>
              <h2 className="font-serif text-2xl md:text-3xl mb-3">
                Last Minute Gift Baskets That Last Year-Long
              </h2>
              <p className="text-white/80 mb-4">
                Mr. RAPsoul.R-SMAHOO signature Gift Baskets, Bowls and Cards - 
                uniquely blending eBooks, Tele-Edu.Therapy, and curated wellness products.
              </p>
              <Button 
                onClick={openStore}
                className="bg-white text-spa-terracotta hover:bg-white/90 rounded-full"
              >
                <ShoppingBag className="w-4 h-4 mr-2" /> Shop Now
              </Button>
            </div>
            <div className="hidden md:block">
              <img 
                src="https://images.unsplash.com/photo-1620077399971-431e7ea0cf0c?w=500"
                alt="Gift Baskets"
                className="rounded-2xl shadow-lg"
              />
            </div>
          </div>
          <div className="absolute -right-20 -bottom-20 w-60 h-60 bg-white/10 rounded-full blur-3xl"></div>
        </div>

        {/* Services */}
        <div className="grid md:grid-cols-3 gap-6">
          {SERVICES.map((service) => (
            <div key={service.id} className="spa-card group cursor-pointer" onClick={openStore}>
              <div className="w-12 h-12 bg-spa-sage/20 rounded-xl flex items-center justify-center mb-4 group-hover:bg-spa-terracotta/10 transition-colors">
                <service.icon className="w-6 h-6 text-spa-moss" strokeWidth={1.5} />
              </div>
              <h3 className="font-serif text-lg text-spa-moss mb-2">{service.name}</h3>
              <p className="text-sm text-spa-text-secondary">{service.description}</p>
            </div>
          ))}
        </div>

        {/* Categories */}
        <div>
          <h2 className="font-serif text-2xl text-spa-moss mb-4">Gift Baskets & Products</h2>
          <div className="flex flex-wrap gap-2 mb-6">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  activeCategory === cat.id
                    ? 'bg-spa-terracotta text-white'
                    : 'bg-spa-muted text-spa-text-secondary hover:bg-spa-border'
                }`}
                data-testid={`category-${cat.id}`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>

        {/* Products Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredBaskets.map((basket) => (
            <div 
              key={basket.id}
              className="spa-card overflow-hidden group cursor-pointer"
              onClick={openStore}
              data-testid={`basket-${basket.id}`}
            >
              <div className="relative h-48 -mx-6 -mt-6 mb-4 overflow-hidden">
                <img 
                  src={basket.image}
                  alt={basket.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
                <div className="absolute top-4 left-4">
                  <span className={`text-xs px-3 py-1 rounded-full capitalize ${CATEGORY_COLORS[basket.category]}`}>
                    {basket.category}
                  </span>
                </div>
                <div className="absolute bottom-4 right-4">
                  <span className="bg-white/90 text-spa-moss font-serif text-lg px-3 py-1 rounded-full">
                    CA${basket.price}
                  </span>
                </div>
              </div>
              <h3 className="font-serif text-lg text-spa-moss mb-2 group-hover:text-spa-terracotta transition-colors">
                {basket.name}
              </h3>
              <p className="text-sm text-spa-text-secondary mb-4">{basket.description}</p>
              <div className="flex flex-wrap gap-2">
                {basket.features.slice(0, 3).map((feature) => (
                  <span 
                    key={feature}
                    className="text-xs px-2 py-1 bg-spa-muted rounded-full text-spa-text-secondary"
                  >
                    {feature}
                  </span>
                ))}
                {basket.features.length > 3 && (
                  <span className="text-xs px-2 py-1 bg-spa-muted rounded-full text-spa-text-secondary">
                    +{basket.features.length - 3} more
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* WHO Alignment Section */}
        <div className="spa-card bg-spa-sage/10">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-spa-sage rounded-xl flex items-center justify-center flex-shrink-0">
              <Heart className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="font-serif text-xl text-spa-moss mb-2">
                Aligned with WHO Mental Health Definition
              </h3>
              <p className="text-spa-text-secondary">
                Our services and products are designed to nurture and maintain your daily adaptive stress 
                responses, promoting a state of being and well-being consistent with the World Health 
                Organization's definition of mental health. Based in Edmonton, Alberta, Canada.
              </p>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center py-8">
          <h3 className="font-serif text-2xl text-spa-moss mb-4">
            Ready to Start Your Self-Care Journey?
          </h3>
          <p className="text-spa-text-secondary mb-6 max-w-xl mx-auto">
            Explore our full collection of gift baskets, eBooks, and wellness programs 
            at the RAPs.ME.WELL Gift Basket Online Store.
          </p>
          <Button 
            onClick={openStore}
            className="spa-button-primary px-8 py-3"
          >
            <Gift className="w-5 h-5 mr-2" /> Visit Gift Basket Store
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
}
